import type { TodoService } from "@common/services/todo-service";
import type { Note} from "@common/types/notes";
import { makeNote } from "@common/types/notes";
import type { TodoId } from "@common/types/todo";
import { makeObservable, observable, runInAction } from "mobx";
import type SpaceStore from "./space-store";
import type UserStore from "@common/stores/user-store";

type ClientNote = Note & {
  _lastLoadTime: number;
  _lastEditTime: number;
  _lastSaveTime: number;
  _error?: string;
};

export interface SharedNotesStoreDependencies {
  spaceStore: SpaceStore;
  userStore: UserStore;
  todoService: TodoService;
}

export default class SharedNotesStore<
  T extends SharedNotesStoreDependencies = SharedNotesStoreDependencies
> {
  @observable
  deps: T;

  @observable
  notesCache: Record<TodoId, ClientNote> = {};

  constructor(dependencies: T) {
    makeObservable(this);
    this.deps = dependencies;
  }

  /**
   * Open the note for a todo. If the note doesn't exist, create it.
   * @returns the note
   */
  public async openNoteForTodo(todoId: TodoId, spaceId: string): Promise<Note> {
    const todoStore = this.deps.spaceStore.todoStoreMap[spaceId];
    if (!todoStore) throw new Error(`space not found: ${spaceId}`);
    const isPlaceholder =
      todoStore.todoIdMap[todoId] && !todoStore.todoIdMap[todoId];

    if (isPlaceholder) {
      // placeholders don't exist on backend yet
      // create on backend
      const placeholderTodo = todoStore.todoIdMap[todoId]!;
      await todoStore.updateTodo(todoId, placeholderTodo, { immediate: true });
    }

    const todo = todoStore.todoIdMap[todoId];

    if (!todo) throw new Error(`todo not found: ${todoId}`);

    if (!todo.notes_id) {
      const newNote = makeNote({
        owner: todo.owner /* overwritten by backend anyways */,
      });
      const { created_id } =
        (await this.deps.todoService.updateNoteForTodo(todoId, newNote)) ?? {};
      if (created_id === newNote.id) {
        todoStore.updateTodo(todoId, { notes_id: created_id });
      } else {
        throw "CREATE_NOTE_FAILED";
      }

      runInAction(() => {
        this.notesCache[todoId] = {
          ...newNote,
          _lastLoadTime: Date.now(),
          _lastEditTime: Date.now(),
          _lastSaveTime: Date.now(),
        };
      });

      return newNote;
    } else {
      const reloadThreshold = 5 * 60; // 5mins
      const cached = this.notesCache[todoId];
      const needsReload =
        Date.now() - (this.notesCache[todoId]?._lastLoadTime ?? 0) <
        reloadThreshold;
      if (cached && !needsReload) return cached;
      const note = await this.deps.todoService.getNoteForTodo(todoId);
      if (!note) {
        throw "NOTE_NOT_FOUND";
      }

      runInAction(() => {
        this.notesCache[todoId] = {
          ...note,
          _lastLoadTime: Date.now(),
          _lastEditTime: Date.now(),
          _lastSaveTime: Date.now(),
        };
      });

      return note;
    }
  }

  async deleteNoteForTodo(todoId: TodoId, spaceId: string) {
    const todoStore = this.deps.spaceStore.todoStoreMap[spaceId];
    if (!todoStore) throw new Error(`space not found: ${spaceId}`);

    const { success } = await this.deps.todoService.deleteNoteForTodo(todoId);
    if (success) {
      const todo = todoStore.fullTodoIdMap[todoId];
      if (!todo) {
        return;
      }
      runInAction(() => {
        todo.notes_id = null;
        delete this.notesCache[todoId];
      });
    }
  }

  async updateNote(
    todoId: TodoId,
    content: string,
    onError: (err: string) => void
  ) {
    const note = this.notesCache[todoId];
    if (!note) return;

    runInAction(() => {
      note.content = content;
      note._lastEditTime = Date.now();
    });

    this.deps.todoService.debouncedUpdateNoteForTodo(
      todoId,
      {
        id: note.id,
        content: note.content,
        owner: this.deps.userStore.userId,
        todoId,
      },
      function onSave() {
        runInAction(() => {
          if (Date.now() > note._lastSaveTime) {
            note._lastSaveTime = Date.now();
            note._error = undefined;
          }
        });
      },
      function onError(err: string) {
        runInAction(() => {
          note._error = err;
        });

        onError(err);
      }
    );
  }
}
